import { RankKit } from "@/components/site/RankKit";
import { NetworkCallout } from "@/components/site/BrandNetwork";
import { createFileRoute } from "@tanstack/react-router";
import { FannieRentalIncomeUpdateSection, FANNIE_SEL_2026_08_FAQS } from "@/components/site/FannieRentalIncomeUpdate";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { TableOfContents, Section, SectionHeader, CTABand, FAQ, StatRow, Breadcrumbs, InlineCTA, FeatureGrid } from "@/components/site/Blocks";
import { TLDR, ProsCons, ExpertInsight } from "@/components/site/SeoBlocks";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/dscr-loan-pros-and-cons";
const HERO = getDscrClusterHero(URL);

const KEYWORDS = [
  "DSCR loan pros and cons",
  "are DSCR loans worth it",
  "DSCR loan disadvantages",
  "DSCR loan benefits",
  "DSCR loan prepayment penalty",
  "DSCR loan rate premium",
  "DSCR loan vs conventional pros and cons",
  "downsides of DSCR loans",
  "DSCR loan risks",
  "is a DSCR loan a good idea",
  "DSCR loan for first time investor",
  "DSCR loan closing costs",
].join(", ");

const FAQS = [
  ...FANNIE_SEL_2026_08_FAQS,
  {
    q: "What is the biggest downside of a DSCR loan?",
    a: "Cost. You generally pay a rate premium over a conventional investment mortgage, and most programs carry a prepayment penalty — commonly a 3–5 year step-down. If you plan to sell or refinance inside that window, price the penalty into the deal.",
  },
  {
    q: "Are DSCR loans worth the higher rate?",
    a: "For an active investor, often yes: no income documentation, no debt-to-income ceiling, unlimited financed properties, and LLC vesting at no premium. For a W-2 borrower with one or two rentals and clean tax returns, a conventional loan is usually cheaper.",
  },
  {
    q: "Can I avoid the prepayment penalty?",
    a: "Some programs offer a shorter penalty term or a buyout for a higher rate or additional points. It is a pricing trade, so ask for both versions on the term sheet and compare them against your hold period.",
  },
  {
    q: "Do DSCR loans hurt my personal credit?",
    a: "They are business-purpose loans and typically are not reported to consumer credit. A credit report is still pulled to set your tier, and members of the entity personally guarantee the note.",
  },
  {
    q: "Is a DSCR loan good for a first-time investor?",
    a: "It can work, though some programs require prior ownership or property-management experience at higher leverage, so first-time investors often see slightly lower maximum LTV.",
  },
  {
    q: "What happens if the property stops cash-flowing after closing?",
    a: "The ratio is tested at underwriting, not monitored monthly, so a vacancy does not trigger a default by itself. You still owe the payment, which is exactly why reserves are required.",
  },
  {
    q: "Do DSCR loans have higher closing costs than conventional loans?",
    a: "Closing costs are generally similar in category — title, escrow, appraisal, lender fees — though DSCR loans can carry an appraisal with a rent schedule and sometimes a slightly higher lender fee that reflects the streamlined underwriting.",
  },
  {
    q: "Can I have too many DSCR loans?",
    a: "No program-wide cap exists the way conventional financing limits financed properties, which is one of the main advantages for scaling investors. Individual lender exposure limits and your own reserve capacity are the practical ceiling.",
  },
  {
    q: "Is it harder to refinance out of a DSCR loan later?",
    a: "Not inherently. You can refinance into another DSCR loan, a conventional loan if you later qualify by income, or pay it off through sale, subject to whatever prepayment penalty period remains on the current note.",
  },
  {
    q: "Do DSCR loans require flood or extra insurance?",
    a: "Standard hazard insurance is always required, and flood insurance is required if the property sits in a designated flood zone, the same as with any mortgage. Some programs also require rent-loss or landlord liability coverage.",
  },
  {
    q: "What is the biggest advantage of a DSCR loan over hard money?",
    a: "Term. DSCR loans are typically 30- or 40-year amortizing products with a materially lower payment than a 6–18 month hard money bridge loan, making them the long-term hold financing rather than a short-term rehab tool.",
  },
  {
    q: "Can I switch from an ARM to a fixed rate DSCR loan later?",
    a: "Not automatically — an ARM does not convert on its own. You would need to refinance into a fixed-rate DSCR loan, which is subject to current pricing, underwriting, and any applicable prepayment penalty on the existing note.",
  },
];

export const Route = createFileRoute("/dscr-loan-pros-and-cons")({
  head: () =>
    buildHead({
      title: "DSCR Loan Pros and Cons: An Honest Investor Breakdown",
      description:
        "The real advantages and real costs of a DSCR loan — no income docs and unlimited properties, weighed against rate premiums, prepayment penalties, and reserve requirements.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      type: "article",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "Pros and Cons", url: URL },
      ],
      service: { name: "DSCR Loan Advisory" },
      faqs: FAQS,
    }),
  component: DscrProsCons,
});

const TRADEOFFS: { title: string; body: string }[] = [
  { title: "Rate premium", body: "Expect to pay more than a conventional investment mortgage. You are buying speed, entity vesting, and the absence of income documentation — price that against the spread rather than the rate alone." },
  { title: "Prepayment penalty", body: "Commonly a 3–5 year step-down. If your plan is a 24-month value-add and exit, either negotiate a shorter term or budget for the penalty." },
  { title: "Reserves tie up cash", body: "1–6 months of PITIA must sit verified at closing, which reduces the capital available for the next acquisition." },
  { title: "Appraisal risk", body: "The rent schedule can come in below your projection and reprice or reduce the loan late in the process — a real risk on properties with no in-place lease." },
  { title: "Business purpose only", body: "The property can never be occupied by you or a family member. Moving in later is a breach of the note, not a paperwork issue." },
  { title: "Leverage is capped", body: "Up to 80% LTV on purchase and 75% on cash-out. Owner-occupied levels of leverage are not available on any DSCR program." },
];

function DscrProsCons() {
  return (
    <SiteShell hideBreadcrumbs productLabel="DSCR Pros & Cons">
      <PageHero
        eyebrow="DSCR · Decision guide"
        title="DSCR loan pros and cons"
        subtitle="A DSCR loan is a tool, not a trophy. Here is the honest ledger — what you gain by qualifying on the property instead of your tax returns, and exactly what it costs you."
        image={HERO}
        primaryHref="/apply?product=dscr"
      />
      <RankKit page="dscr-loan-pros-and-cons" part="answer" />
      <PageEssentials hideAnswer hideTldr hideToc />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR Loans", href: "/dscr-loans" }, { name: "Pros & Cons" }]} />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#tradeoffs", label: "Trade-offs" },
          { href: "#fit", label: "Who it fits" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <Section id="tldr">
        <TLDR
          points={[
            "Pro: no tax returns, no W-2s, no debt-to-income ceiling — the property qualifies itself.",
            "Pro: unlimited financed properties and LLC vesting at no pricing premium.",
            "Con: a rate premium over conventional, plus a prepayment penalty on most programs.",
            "Con: 1–6 months of PITIA reserves must be verified and left liquid at closing.",
            "Net: strongest for active, entity-vested, self-employed, or high-property-count investors.",
          ]}
        />
        <div className="mt-8">
          <StatRow
            items={[
              { label: "Income docs", value: "None" },
              { label: "Property limit", value: "Unlimited" },
              { label: "Max LTV", value: "80%" },
              { label: "Typical prepay", value: "3–5 yr step-down" },
            ]}
          />
        </div>
      </Section>

      <ProsCons
        title="The ledger at a glance"
        pros={[
          "No tax returns, W-2s, or pay stubs — and no debt-to-income calculation",
          "Unlimited financed properties, unlike agency caps",
          "LLC, LP, or corporate vesting at no pricing premium",
          "Self-employed, 1099, retired, and foreign national investors are not penalized",
          "Faster underwriting because the income review is the appraisal, not your returns",
          "Cash-out proceeds can be used for the next acquisition without a use-of-funds test",
        ]}
        cons={[
          "Higher rate than a conventional investment mortgage",
          "Prepayment penalty on most programs, typically 3–5 years",
          "20–25% down, plus verified reserves that stay liquid",
          "Business purpose only — never a primary or second home",
          "A weak appraisal rent schedule can reprice the deal late",
          "Small loan balances price worse because fixed costs spread over less principal",
        ]}
      />

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>

      <div className="bg-muted/40">
        <Section id="tradeoffs">
          <SectionHeader
            eyebrow="The costs, in detail"
            title="Every trade-off, spelled out"
            intro="None of these are dealbreakers on their own. They become expensive when they surprise you at the closing table."
          />
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {TRADEOFFS.map((t) => (
              <div key={t.title} className="rounded-xl border border-border bg-card p-5">
                <div className="font-display text-base font-semibold text-foreground">{t.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{t.body}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <LiveDscrRateSection
            strategyCards
        id="live-rates"
        eyebrow={"Live pricing · The cost side"}
        heading={"What DSCR Financing Actually Costs Today"}
        paragraphs={[
            "The trade with DSCR financing is generally cost: investment-property, cash-flow-qualified financing is typically priced above comparable owner-occupied conventional financing, though the actual difference depends on both files. Rather than describe that in the abstract, the live options below show what the pricing engine last returned for a qualifying rental scenario.",
            "Weigh the rate against what the structure offers — many programs qualify without personal tax returns, allow entity vesting and are more flexible than agency financing on the number of financed properties, though each of those is program-specific — and against the fee and credit columns, which can matter more than a tenth of a point on a short hold.",
        ]}
        faqs={[
            { q: "Is a DSCR loan worth the higher rate?", a: "That depends on the deal's cash flow, your hold period and your alternatives. We can price both the DSCR option and any conventional alternative you qualify for so the comparison is concrete rather than theoretical." },
            { q: "Are DSCR closing costs higher too?", a: "Lender fee structures vary by investor. The primary lender fee is shown with each quote and other estimated lender and closing costs are itemized separately in the rate center." },
        ]}
        links={[
            { to: "/investment-property-rates", label: "See the live cost of DSCR financing" },
            { to: "/dscr-loan-rates", label: "Compare pricing tiers" },
            { to: "/dscr-loans", label: "DSCR loan overview" },
        ]}
      />

      <FannieRentalIncomeUpdateSection />

      <Section id="fit">
        <SectionHeader eyebrow="Who it fits" title="When a DSCR loan is the right call — and when it is not" />
        <div className="mt-8">
          <FeatureGrid
            items={[
              { title: "Strong fit: self-employed", body: "Aggressive depreciation and write-offs sink a debt-to-income file. A DSCR loan never looks at the return." },
              { title: "Strong fit: 5+ properties", body: "Agency financing caps out at ten financed properties. DSCR programs have no count limit." },
              { title: "Strong fit: entity vesting", body: "Title held in an LLC is standard here and disqualifying on conventional financing." },
              { title: "Weaker fit: W-2 with one rental", body: "Clean returns and low DTI usually price better on a conventional investment loan." },
              { title: "Weaker fit: short hold", body: "A 12–18 month flip runs into the prepayment penalty — a bridge or fix and flip loan is the better structure." },
              { title: "Weaker fit: thin reserves", body: "If the reserve requirement drains the account you need for the next deal, wait a quarter." },
            ]}
          />
        </div>
      </Section>

      <InlineCTA
        variant="soft"
        title="Want the trade-off priced on your actual deal?"
        body="Send the address, rent, and hold period — we'll show the DSCR structure next to the alternative so you can see the real spread."
        href="/apply?product=dscr"
      />


      <DscrProgramLinks currentPath={URL} />

      <RankKit page="dscr-loan-pros-and-cons" part="details" />
      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="Pros and cons questions investors ask" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          Guidelines shown are illustrative and vary by lender, property, and state. Eligibility, terms, and availability
          are subject to underwriting and approval. Not a commitment to lend. Business-purpose loans only.
        </p>
      </Section>

      <DscrCrossProductLinks currentPath={URL} />
      <NetworkCallout
        title="Planning to live in one of the units?"
        body="DSCR loans are for non-owner-occupied investment property only. If you plan to live in a 2–4 unit property, owner-occupied FHA or VA financing and down payment assistance may fit better — our sister sites explain those options."
        sites={["Simply Approved FHA Loans", "Simply Approved VA Loans", "DPA Loan Programs"]}
      />

      <ExpertInsight insightKey="dscr-pros-cons" />

      <CTABand
        title="Compare a DSCR structure against the alternative"
        body="A short pre-qualification request is all it takes to get a scenario review."
        href="/apply?product=dscr"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
